import { useEffect } from 'react';
import type { Theme } from '@ogaim/ui';

/**
 * Pushes the active theme into the CSS custom properties the desktop shell
 * uses.
 *
 * The shell (wallpaper, taskbar, Start menu, window frames) is plain DOM, so
 * it cannot read the React theme context. Rather than duplicate the palette in
 * CSS, the stylesheet declares variables and this writes the current values
 * onto :root — one source of truth, and switching is a handful of property
 * writes rather than a re-render of the shell.
 */
export function useCssTheme(theme: Theme): void {
  useEffect(() => {
    const root = document.documentElement;
    const c = theme.colors;

    const vars: Record<string, string> = {
      '--face': c.face,
      '--highlight': c.highlight,
      '--light': c.light,
      '--shadow': c.shadow,
      '--dark': c.darkShadow,
      '--window-text': c.windowText,
      '--disabled-text': c.disabledText,
      '--field-bg': c.fieldBg,
      '--title-from': c.titleActiveLeft,
      '--title-to': c.titleActiveRight,
      '--title-inactive-from': c.titleInactiveLeft,
      '--title-inactive-to': c.titleInactiveRight,
      '--title-text': c.titleText,
      '--title-text-inactive': c.titleTextInactive,
      '--selection-bg': c.selectionBg,
      '--selection-text': c.selectionText,
      '--desktop': c.desktop,
      '--accent': c.accent,
      '--ui-font': `'${theme.fonts.ui}', 'MS Sans Serif', Tahoma, Geneva, sans-serif`,
      '--ui-size': `${theme.fontSizes.ui}px`,
      '--taskbar-height': `${theme.metrics.titleBarHeight + 10}px`,
      '--radius': `${theme.metrics.radius}px`,
      // Platinum centres its caption text and puts the buttons on the left.
      '--title-align': theme.titleBar.align === 'center' ? 'center' : 'left',
    };

    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
    }
    root.setAttribute('data-theme', theme.id);
  }, [theme]);
}
